<script>
import axios from '@/utils/axios';

const DEFAULT_FORM = () => ({
  id: null,
  leave_type_id: '',
  branch_id: '',
  days_allowed: 0,
  year_type: 'financial',
  carry_forward: false,
  max_carry_forward_days: 0,
  allow_half_day: false,
  effective_from: '',
});

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      form: DEFAULT_FORM(),
      errors: {},
      deleteItem: null,
      leaveTypes: [],
      branches: [],
      yearTypes: [
        { value: 'financial', label: 'Financial Year' },
        { value: 'calendar', label: 'Calendar Year' },
      ],
    };
  },
  mounted() {
    this.fetchMeta();
  },
  methods: {
    async fetchMeta() {
      try {
        const [ltRes, brRes] = await Promise.allSettled([
          axios.get('/hrms/leave-types'),
          axios.get('/branches'),
        ]);
        if (ltRes.status === 'fulfilled') this.leaveTypes = ltRes.value.data.data.data || [];
        if (brRes.status === 'fulfilled') this.branches = brRes.value.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    leaveTypeLabel(lt) {
      return lt.code ? `${lt.name} (${lt.code})` : lt.name;
    },
    branchLabel(b) {
      return b.name || b.branch_name || b.title || `Branch #${b.id}`;
    },

    add() {
      this.resetForm();
    },

    edit(item) {
      this.resetForm();
      this.form.id = item.id;
      this.form.leave_type_id = item.leave_type_id || '';
      this.form.branch_id = item.branch_id || '';
      this.form.days_allowed = item.days_allowed ?? 0;
      this.form.year_type = item.year_type || 'financial';
      this.form.carry_forward = !!item.carry_forward;
      this.form.max_carry_forward_days = item.max_carry_forward_days ?? 0;
      this.form.allow_half_day = !!item.allow_half_day;
      this.form.effective_from = item.effective_from || '';
    },

    setDelete(item) {
      this.deleteItem = item;
    },

    buildPayload() {
      return {
        leave_type_id: this.form.leave_type_id || null,
        branch_id: this.form.branch_id || null,
        days_allowed: this.form.days_allowed,
        year_type: this.form.year_type,
        carry_forward: this.form.carry_forward,
        // carry_forward off hai to days 0 bhejo
        max_carry_forward_days: this.form.carry_forward ? this.form.max_carry_forward_days : 0,
        allow_half_day: this.form.allow_half_day,
        effective_from: this.form.effective_from || null,
      };
    },

    async save() {
      this.errors = {};
      this.setLoading(true);
      try {
        let response;
        if (this.form.id) {
          response = await axios.put(`/hrms/leave-policies/${this.form.id}`, this.buildPayload());
        } else {
          response = await axios.post('/hrms/leave-policies', this.buildPayload());
        }
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_leave_policy_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async deletePolicy() {
      this.setLoading(true);
      try {
        const response = await axios.delete(`/hrms/leave-policies/${this.deleteItem.id}`);
        this.$swalToast(response.data.message);
        this.$emit('updated');
        this.$closeModal('hrm_leave_policy_delete_modal');
      } catch (error) {
        if (error.type === 'validation') {
          this.$swalToast(error.errors?.id?.[0] || error.message, 'error');
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    resetForm() {
      this.form = DEFAULT_FORM();
      this.errors = {};
    },
  },
};
</script>

<template>
  <!-- Add/Edit Modal -->
  <div class="modal fade" id="hrm_leave_policy_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ form.id ? 'Edit' : 'Add' }} Leave Policy</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="save">
          <div class="modal-body pb-0">
            <div class="row">

              <!-- Leave Type -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Leave Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.leave_type_id">
                  <option value="">Select Leave Type</option>
                  <option v-for="lt in leaveTypes" :key="lt.id" :value="lt.id">{{ leaveTypeLabel(lt) }}</option>
                </select>
                <div v-if="errors.leave_type_id" class="text-danger small mt-1">{{ errors.leave_type_id[0] }}</div>
              </div>

              <!-- Branch -->
              <div class="col-md-6 mb-3">
                <label class="form-label">Branch</label>
                <select class="form-select" v-model="form.branch_id">
                  <option value="">All Branches</option>
                  <option v-for="b in branches" :key="b.id" :value="b.id">{{ branchLabel(b) }}</option>
                </select>
                <div v-if="errors.branch_id" class="text-danger small mt-1">{{ errors.branch_id[0] }}</div>
              </div>

              <!-- Days Allowed -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Days Allowed <span class="text-danger">*</span></label>
                <input type="number" min="0" step="0.5" class="form-control" v-model.number="form.days_allowed" />
                <div v-if="errors.days_allowed" class="text-danger small mt-1">{{ errors.days_allowed[0] }}</div>
              </div>

              <!-- Year Type -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Year Type <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.year_type">
                  <option v-for="yt in yearTypes" :key="yt.value" :value="yt.value">{{ yt.label }}</option>
                </select>
                <div v-if="errors.year_type" class="text-danger small mt-1">{{ errors.year_type[0] }}</div>
              </div>

              <!-- Effective From -->
              <div class="col-md-4 mb-3">
                <label class="form-label">Effective From <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="form.effective_from" />
                <div v-if="errors.effective_from" class="text-danger small mt-1">{{ errors.effective_from[0] }}</div>
              </div>

              <!-- Allow Half Day -->
              <div class="col-md-4 mb-3">
                <label class="form-label d-block">Allow Half Day?</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" role="switch" v-model="form.allow_half_day" id="lpHalfDay" />
                  <label class="form-check-label ms-1" for="lpHalfDay">{{ form.allow_half_day ? 'Yes' : 'No' }}</label>
                </div>
              </div>

              <!-- Carry Forward -->
              <div class="col-md-4 mb-3">
                <label class="form-label d-block">Carry Forward?</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input" type="checkbox" role="switch" v-model="form.carry_forward" id="lpCarry" />
                  <label class="form-check-label ms-1" for="lpCarry">{{ form.carry_forward ? 'Yes' : 'No' }}</label>
                </div>
              </div>

              <!-- Max Carry Forward Days (sirf carry_forward ON pe) -->
              <div class="col-md-4 mb-3" v-if="form.carry_forward">
                <label class="form-label">Max Carry Forward Days</label>
                <input type="number" min="0" step="0.5" class="form-control" v-model.number="form.max_carry_forward_days" />
                <div v-if="errors.max_carry_forward_days" class="text-danger small mt-1">{{ errors.max_carry_forward_days[0] }}</div>
              </div>

            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ form.id ? 'Update' : 'Save' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="hrm_leave_policy_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">You want to delete this leave policy? This can't be undone.</p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deletePolicy">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>